import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Provider } from "react-redux";
import Waypoint from "react-waypoint";
import { isEmpty, isNil } from "ramda";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

import { getPreviewToken } from "get_base_uri";

import { updateStoreInfo } from "actions/online_ordering/store_info";
import Loader from "components/loader";
import Footer from "components/footer";

import CartContainer from "containers/online_ordering/cart_container";
import HeaderContainer from "containers/online_ordering/header_container";
import MainViewContainer from "containers/online_ordering/main_view_container";
import PreviewContainer from "containers/online_ordering/preview_container";

import trackAnalytic from "helpers/analytics";
import FreezeBody from "helpers/context/freeze_body";
import FeeContext from "helpers/context/fees";
import {
  hasFeatureFlagEnabled,
  LdFlags,
  updatedStoreInfoPerFlags,
} from "helpers/online_ordering";
import { OnlineOrderingStatus } from "./online_ordering/online_ordering_status";

function OnlineOrdering({ store, flags }) {
  useEffect(() => {
    window.onerror = (message, source, line, col, error) => {
      trackAnalytic("Error", {
        message,
        source,
        line,
        col,
        error,
      });
      return false;
    };
    // This will set the property values: https://github.com/segmentio/analytics.js/blob/master/analytics.js#L587
    window.analytics.page({ referrer: null, search: null, url: null });
  }, []);

  const [bodyIsFrozen, setFreezeBody] = useState(false);
  const [freezeBodyScrollReturn, setFreezeBodyScrollReturn] = useState(0);
  const freezeContext = useMemo(
    () => ({
      freezeBody: () => {
        // Save current scroll position
        setFreezeBodyScrollReturn(window.scrollY);
        setFreezeBody(true);
      },
      unFreezeBody: () => setFreezeBody(false),
    }),
    [setFreezeBody, setFreezeBodyScrollReturn]
  );
  useEffect(() => {
    if (!bodyIsFrozen && freezeBodyScrollReturn) {
      window.scrollTo(0, freezeBodyScrollReturn);
      setFreezeBodyScrollReturn(0);
    }
  }, [bodyIsFrozen, freezeBodyScrollReturn, setFreezeBodyScrollReturn]);

  const [cartIsSticky, setStickCart] = useState(false);
  const stickCart = useCallback(() => setStickCart(true), [setStickCart]);
  const unStickCart = useCallback(() => setStickCart(false), [setStickCart]);

  if (!store?.getState()?.storeInfo || isEmpty(flags)) {
    return (
      <div className="app">
        <Loader />
      </div>
    );
  }

  const state = store.getState();
  if (!isEmpty(flags)) {
    store.dispatch(
      updateStoreInfo(updatedStoreInfoPerFlags(state.storeInfo, flags))
    );
  }
  // Manually set page title to restaurant name on load
  document.title = state.storeInfo.locationName;

  let previewBanner;
  if (!isNil(getPreviewToken()) && !state.storeInfo.published) {
    previewBanner = <PreviewContainer />;
  }

  const useExtendedOrderAhead = hasFeatureFlagEnabled(
    flags,
    LdFlags.EXTENDED_ORDER_AHEAD
  );

  const mainContent = (
    <div className="content">
      <OnlineOrderingStatus />
      <MainViewContainer />
      <FeeContext.Provider value={flags.oloConsumerFeePercentage}>
        <CartContainer
          cartClass={cartIsSticky ? "fixed-cart" : ""}
          useExtendedOrderAhead={useExtendedOrderAhead}
          key="cart"
        />
      </FeeContext.Provider>
    </div>
  );

  return (
    <Provider store={store}>
      <FreezeBody.Provider value={freezeContext}>
        <div className={bodyIsFrozen ? "freeze-body" : ""}>
          {previewBanner}
          <div
            className="app"
            style={
              bodyIsFrozen ? { top: `-${freezeBodyScrollReturn}px` } : null
            }>
            <HeaderContainer />
            <Waypoint onLeave={stickCart} onEnter={unStickCart} />
            {mainContent}
            <Footer />
          </div>
        </div>
      </FreezeBody.Provider>
    </Provider>
  );
}

OnlineOrdering.propTypes = {
  store: PropTypes.object,
  request: PropTypes.object,
  flags: PropTypes.object.isRequired,
};

export default withLDConsumer()(OnlineOrdering);
